import type { DebitCardAssignment } from '../types';

function findCardBySequenceId(
  cards: DebitCardAssignment[],
  sequenceCardId: string,
): DebitCardAssignment | null {
  return (
    cards.find(
      c =>
        c.isActive &&
        c.assignedUserId &&
        (c.sequencePlatformCardId === sequenceCardId ||
          c.sequenceCardId === sequenceCardId),
    ) || null
  );
}

/** Parse trailing 4 digits or last 4-digit group from a QB COA / card name. */
export function extractCardLastFour(bankAccountName: string | null | undefined): string | null {
  if (!bankAccountName || typeof bankAccountName !== 'string') return null;
  const trimmed = bankAccountName.trim();
  if (!trimmed) return null;
  const trailing = trimmed.match(/(\d{4})\s*$/);
  if (trailing) return trailing[1];
  const groups = trimmed.match(/\d{4}/g);
  if (groups && groups.length > 0) return groups[groups.length - 1];
  return null;
}

/** Parse `**1234` suffix Sequence puts on merchant descriptions. */
export function extractLastFourFromSequenceText(text: string | null | undefined): string | null {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const pipeSuffix = trimmed.match(/\|\s*\*{2}(\d{4})\s*$/);
  if (pipeSuffix) return pipeSuffix[1];
  const starSuffix = trimmed.match(/\*{2}(\d{4})\s*$/);
  if (starSuffix) return starSuffix[1];
  return null;
}

/** Hide redundant `| **1234` card suffix from Sequence merchant lines. */
export function stripSequenceCardSuffix(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/\s*\|\s*\*{2}\d{4}\s*$/g, '')
    .replace(/\s*\*{2}\d{4}\s*$/g, '')
    .trim();
}

function findCardAssignment(
  cards: DebitCardAssignment[],
  lastFour: string,
  bankAccountName?: string | null,
  bankAccountId?: string | null,
): DebitCardAssignment | null {
  const active = cards.filter(c => c.isActive && c.lastFour === lastFour);
  if (active.length === 0) return null;
  if (active.length === 1) return active[0];
  if (bankAccountId) {
    const byId = active.find(c => c.bankAccountId === bankAccountId);
    if (byId) return byId;
  }
  if (bankAccountName) {
    const byName = active.find(c => c.bankAccountName === bankAccountName);
    if (byName) return byName;
  }
  return active[0];
}

/** Resolve cardholder from debit card manifest only (ignores stale assignedUserId on the expense). */
function resolveManifestCardholder(
  args: {
    bankAccountName?: string | null;
    bankAccountId?: string | null;
    cardLastFour?: string | null;
    description?: string | null;
    sequenceCardId?: string | null;
  },
  cards: DebitCardAssignment[],
): CardholderMatch | null {
  if (args.sequenceCardId) {
    const seqCard = findCardBySequenceId(cards, args.sequenceCardId);
    if (seqCard) {
      return {
        userId: seqCard.assignedUserId,
        userName: seqCard.assignedUserName,
        pendingCategorization: true,
        sequenceHolderName: seqCard.sequenceHolderName,
      };
    }
  }

  const lastFour =
    args.cardLastFour ||
    extractLastFourFromSequenceText(args.description) ||
    extractLastFourFromSequenceText(args.bankAccountName) ||
    extractCardLastFour(args.bankAccountName);
  if (!lastFour) return null;

  const card = findCardAssignment(cards, lastFour, args.bankAccountName, args.bankAccountId);
  if (!card) return null;

  return {
    userId: card.assignedUserId,
    userName: card.assignedUserName,
    pendingCategorization: true,
    sequenceHolderName: card.sequenceHolderName,
  };
}

export interface CardholderMatch {
  userId: string;
  userName: string;
  pendingCategorization: boolean;
  /** When resolved via Sequence registry. */
  sequenceHolderName?: string;
}

export function resolveCardholderForTransaction(
  args: {
    bankAccountName?: string | null;
    bankAccountId?: string | null;
    cardLastFour?: string | null;
    assignedUserId?: string | null;
    assignedUserName?: string | null;
    categorizationStatus?: string | null;
    description?: string | null;
    sequenceCardId?: string | null;
  },
  cards: DebitCardAssignment[],
): CardholderMatch | null {
  const manifest = resolveManifestCardholder(args, cards);
  if (manifest) {
    return {
      ...manifest,
      pendingCategorization: args.categorizationStatus === 'pending',
    };
  }

  if (args.assignedUserId) {
    return {
      userId: args.assignedUserId,
      userName:
        args.assignedUserName ||
        cards.find(c => c.assignedUserId === args.assignedUserId)?.assignedUserName ||
        'Cardholder',
      pendingCategorization: args.categorizationStatus === 'pending',
    };
  }

  return null;
}
